import { useEffect, useMemo, useRef, useState } from 'react';

import {
  getStoreReviewDefaultMinimumQuantity,
  getStoreReviewPricingBreakpoints,
  needsStoreReviewBreakpointPricing,
} from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeReviewPricing.utils';
import { quoteStoreReviewDesignGroupBreakpointPriceLists } from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeReviewQuote.utils';
import {
  isStoreCartDesignGroupMoqPending,
  shouldShowStoreCartDesignGroupMoqTiers,
} from '@/modules/Cart/StoreCart/helpers/storeCartSetup.utils';
import type { DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS } from '@/modules/Cart/StoreCart/storeCart.constants';
import type { DesignGroup } from '@/modules/Orders/CreateOrders/StoreCheckout/types/storeReviewOrderStep.types';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { IQuoteBreakpoints } from '@/common/interfaces/quoter.interface';
import { useGetQuoteMutation } from '@/api/QuoterQuery';

import type { StoreCartMoqShippingLane } from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeCheckoutMoqShipping.utils';

export interface IStoreReviewBreakdownPricingOverlay {
  price?: number;
  priceList?: IQuoteBreakpoints[];
  minQuant?: number;
  baseUnitPrice?: number;
}

interface IUseStoreReviewBreakdownPricingParams {
  showBreakdownModal: { isOpen: boolean; groupIndex: number | null };
  designGroups: DesignGroup[];
  selectedMinimums: Record<number, number>;
  allItems: ICartItemDetail[];
  isPricingReadOnly: boolean;
  moqShippingLane: StoreCartMoqShippingLane;
  getDesignGroupMoq: (group: DesignGroup) => number | undefined;
  getDesignGroupMoqApiStatus: (
    group: DesignGroup
  ) => typeof DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS;
}

export const useStoreReviewBreakdownPricing = ({
  showBreakdownModal,
  designGroups,
  selectedMinimums,
  allItems,
  isPricingReadOnly,
  moqShippingLane,
  getDesignGroupMoq,
  getDesignGroupMoqApiStatus,
}: IUseStoreReviewBreakdownPricingParams): {
  breakdownPricingByItemId: Record<number, IStoreReviewBreakdownPricingOverlay>;
  isBreakdownPricingLoading: boolean;
} => {
  const [getQuote] = useGetQuoteMutation();
  const [breakdownPricingByItemId, setBreakdownPricingByItemId] = useState<
    Record<number, IStoreReviewBreakdownPricingOverlay>
  >({});
  const [isBreakdownPricingLoading, setIsBreakdownPricingLoading] = useState(false);
  const quoteRequestIdRef = useRef(0);
  const lastFetchedRequestKeyRef = useRef<string | null>(null);
  const designGroupsRef = useRef(designGroups);
  const allItemsRef = useRef(allItems);

  useEffect(() => {
    designGroupsRef.current = designGroups;
  }, [designGroups]);

  useEffect(() => {
    allItemsRef.current = allItems;
  }, [allItems]);

  const openGroupPricingKey = useMemo(() => {
    if (!showBreakdownModal.isOpen || showBreakdownModal.groupIndex == null) {
      return '';
    }

    const group = designGroups[showBreakdownModal.groupIndex];

    if (!group) {
      return '';
    }

    return group.items
      .map(
        (item) =>
          `${item.id}:${item.minQuant ?? 0}:${item.price ?? 0}:${item.priceList?.length ?? 0}`
      )
      .join('|');
  }, [designGroups, showBreakdownModal.groupIndex, showBreakdownModal.isOpen]);

  useEffect(() => {
    lastFetchedRequestKeyRef.current = null;
  }, [moqShippingLane]);

  useEffect(() => {
    if (!showBreakdownModal.isOpen || showBreakdownModal.groupIndex == null) {
      setIsBreakdownPricingLoading(false);
      setBreakdownPricingByItemId({});
      lastFetchedRequestKeyRef.current = null;
      return;
    }

    if (isPricingReadOnly) {
      setIsBreakdownPricingLoading(false);
      return;
    }

    const { groupIndex } = showBreakdownModal;
    const group = designGroupsRef.current[groupIndex];

    if (!group) {
      setIsBreakdownPricingLoading(false);
      return;
    }

    const apiStatus = getDesignGroupMoqApiStatus(group);
    const designGroupMoq = getDesignGroupMoq(group);
    const hasMoqTiers = shouldShowStoreCartDesignGroupMoqTiers(apiStatus);

    if (hasMoqTiers && isStoreCartDesignGroupMoqPending(apiStatus)) {
      setIsBreakdownPricingLoading(false);
      return;
    }

    const { quantities } = getStoreReviewPricingBreakpoints(
      group,
      hasMoqTiers ? designGroupMoq : undefined
    );
    const defaultMinimum = getStoreReviewDefaultMinimumQuantity(group, quantities);
    const selectedMinimum = selectedMinimums[groupIndex] ?? defaultMinimum;
    const filteredQuantities = quantities.filter((qty) => qty >= selectedMinimum);

    const needsQuote = group.items.some((item) =>
      needsStoreReviewBreakpointPricing(item, filteredQuantities)
    );

    const requestKey = `${moqShippingLane}:${groupIndex}:${selectedMinimum}:${group.items
      .map((item) => item.id)
      .join(',')}`;

    if (!needsQuote) {
      setIsBreakdownPricingLoading(false);
      lastFetchedRequestKeyRef.current = requestKey;
      return;
    }

    if (lastFetchedRequestKeyRef.current === requestKey) {
      return;
    }

    const requestId = quoteRequestIdRef.current + 1;
    quoteRequestIdRef.current = requestId;
    setIsBreakdownPricingLoading(true);

    quoteStoreReviewDesignGroupBreakpointPriceLists(
      group,
      allItemsRef.current,
      filteredQuantities,
      getQuote,
      { storeShippingLane: moqShippingLane }
    )
      .then((quotedItems) => {
        if (quoteRequestIdRef.current !== requestId) {
          return;
        }

        const nextOverlay = quotedItems.reduce<Record<number, IStoreReviewBreakdownPricingOverlay>>(
          (acc, quotedItem) => {
            acc[quotedItem.id] = {
              price: quotedItem.price,
              priceList: quotedItem.priceList,
              minQuant: quotedItem.minQuant,
              baseUnitPrice: quotedItem.baseUnitPrice,
            };

            return acc;
          },
          {}
        );

        setBreakdownPricingByItemId(nextOverlay);

        const stillNeedsQuote = quotedItems.some((item) =>
          needsStoreReviewBreakpointPricing(item, filteredQuantities)
        );

        if (!stillNeedsQuote) {
          lastFetchedRequestKeyRef.current = requestKey;
        }
      })
      .finally(() => {
        if (quoteRequestIdRef.current === requestId) {
          setIsBreakdownPricingLoading(false);
        }
      })
      .catch(() => undefined);
  }, [
    getDesignGroupMoq,
    getDesignGroupMoqApiStatus,
    getQuote,
    isPricingReadOnly,
    moqShippingLane,
    openGroupPricingKey,
    selectedMinimums,
    showBreakdownModal,
  ]);

  return { breakdownPricingByItemId, isBreakdownPricingLoading };
};
